import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useBrand } from './brand';
import { useTour } from './tour';
import { titleCase, upcomingClock } from './lib/format';
import { anyWithinSixMinutes, useNow } from './lib/useNow';
import { useTrack, useTrackExpand } from './track';
import { ChatBarButton } from './chat/ChatLauncher';
import './chrome.css';

export interface NavItem {
  label: string;
  to?: string;
  onClick?: () => void;
  /** 'feature' is the highlighted high-probability link; 'danger' is sign-out. */
  kind?: 'plain' | 'feature' | 'danger';
  /** Only in the phone menu. */
  mobileOnly?: boolean;
}

function NavLink({ item, onDone }: { item: NavItem; onDone?: () => void }) {
  const track = useTrack();
  const cls = `ui-nav-item ${item.kind ?? 'plain'}`;
  const clicked = () => {
    track('ui_click', { target: 'nav', label: item.label });
    onDone?.();
  };
  const body = (
    <>
      {item.kind === 'feature' && <i aria-hidden="true" />}
      {item.label}
    </>
  );
  if (item.to) {
    return (
      <Link to={item.to} className={cls} onClick={clicked}>
        {body}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      onClick={() => {
        clicked();
        item.onClick?.();
      }}
    >
      {body}
    </button>
  );
}

/** Brand header: logo and tagline, the tour button, and the viewer's links. */
export function AppHeader({ links = [], greeting, homeHref = '/' }: { links?: NavItem[]; greeting?: string; homeHref?: string }) {
  const brand = useBrand();
  const tour = useTour();
  const expand = useTrackExpand();
  const track = useTrack();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const desktop = links.filter((l) => !l.mobileOnly);
  return (
    <header className="ui-ah">
      <Link to={homeHref} className="ui-brand" aria-label={`${brand.name} home`}>
        {brand.logo}
        <span className="ui-tag">{brand.tagline}</span>
      </Link>
      {brand.features.tour && tour.available && (
        <button
          type="button"
          className="ui-help"
          aria-label="Help: a tour of this page"
          title="Tour this page"
          onClick={() => {
            track('tour_start');
            tour.start();
          }}
        >
          ?
        </button>
      )}
      {links.length > 0 && (
        <>
          <nav className="ui-nav" aria-label="Account">
            {greeting && <span className="ui-greet">{greeting}</span>}
            {desktop.map((l) => (
              <NavLink key={l.label} item={l} />
            ))}
          </nav>
          <div className="ui-menu-wrap" ref={menuRef}>
            <button
              type="button"
              className="ui-menu"
              aria-label="Menu"
              aria-expanded={open}
              onClick={() => {
                setOpen(!open);
                expand('nav_menu', !open);
              }}
            >
              ☰
            </button>
            {open && (
              <div className="ui-menu-list">
                {greeting && <div className="ui-greet">{greeting}</div>}
                {links.map((l) => (
                  <NavLink key={l.label} item={l} onDone={() => setOpen(false)} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </header>
  );
}

/** The date bar: the day on the left, previous / today / next on the right, plus an optional extra control. */
export function DateBar({
  label,
  shortLabel,
  isToday,
  onPrev,
  onToday,
  onNext,
  extra,
}: {
  label: string;
  shortLabel?: string;
  isToday: boolean;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
  extra?: ReactNode;
}) {
  const track = useTrack();
  const go = (direction: 'previous' | 'today' | 'next', fn: () => void) => () => {
    track('ui_click', { target: 'date_nav', direction });
    fn();
  };
  return (
    <div className="ui-db">
      <div className="ui-day">
        <span className="long">{label}</span>
        <span className="short">{shortLabel ?? label}</span>
      </div>
      {extra}
      <div className="ui-db-nav">
        <button type="button" aria-label="Previous day" onClick={go('previous', onPrev)}>
          ‹ <span className="long">Previous</span>
        </button>
        <button type="button" className={isToday ? 'on' : ''} onClick={go('today', onToday)}>
          Today
        </button>
        <button type="button" aria-label="Next day" onClick={go('next', onNext)}>
          <span className="long">Next</span> ›
        </button>
      </div>
    </div>
  );
}

export interface CtaLink {
  label: string;
  to: string;
  primary?: boolean;
}

/** Shown to signed-out visitors in place of the date bar. */
export function SignedOutBar({ message, actions }: { message?: string; actions: CtaLink[] }) {
  return (
    <div className="ui-db ui-signedout">
      {message && <span className="msg">{message}</span>}
      <div className="ui-db-nav">
        {actions.map((a) => (
          <Link key={a.to} to={a.to} className={a.primary ? 'primary' : ''}>
            {a.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Footer links plus the gambling-help notice every page must carry. */
export function SiteFooter({ siteName, links }: { siteName: string; links: CtaLink[] }) {
  return (
    <>
      <footer className="ui-footer">
        <span>
          © {new Date().getFullYear()} {siteName}. All rights reserved.
        </span>
        <nav aria-label="Site">
          {links.map((l) => (
            <Link key={l.to} to={l.to}>
              {l.label}
            </Link>
          ))}
        </nav>
      </footer>
      <div className="ui-gambling-help">
        <b>WHAT'S GAMBLING REALLY COSTING YOU?</b>
        <span>
          For free and confidential support call <a href="tel:1800858858">1800 858 858</a> or visit{' '}
          <a href="https://www.gamblinghelponline.org.au/" target="_blank" rel="noopener noreferrer">
            gamblinghelponline.org.au
          </a>
        </span>
      </div>
    </>
  );
}

export interface DrawerMeeting {
  key: string;
  name: string;
  state: string;
  /** metropolitan | provincial | anything else (country). */
  type: string | null | undefined;
  href: string;
  /** Start of the meeting's next race (ISO); inside five minutes the drawer counts it down. */
  next?: string | null;
}

const TRACK_TYPES: [string, string][] = [
  ['metropolitan', 'Metropolitan'],
  ['provincial', 'Provincial'],
  ['country', 'Country'],
];

export const trackTypeOf = (type: string | null | undefined): string => {
  const t = (type ?? '').toLowerCase();
  return t === 'metropolitan' || t === 'provincial' ? t : 'country';
};

/** Bottom drawer listing the day's meetings by track type. The bar stays put; the list slides up. */
export function TracksDrawer({
  title,
  subtitle,
  meetings,
  currentKey,
}: {
  title: string;
  subtitle: string;
  meetings: DrawerMeeting[];
  currentKey?: string;
}) {
  const [open, setOpen] = useState(false);
  const track = useTrack();
  const expand = useTrackExpand();
  const now = useNow(anyWithinSixMinutes(meetings.map((m) => m.next)) ? 1000 : 30000);
  // a meeting's countdown: red inside five minutes, Now at the jump, gone 45 seconds later
  const clockOf = (m: DrawerMeeting) => {
    if (!m.next || Date.parse(m.next) + 45000 <= now) return null;
    const c = upcomingClock(m.next, now);
    return c.urgent ? c.text : null;
  };
  const anySoon = meetings.some((m) => clockOf(m));
  return (
    <div className="ui-drawer" data-open={open} data-tour="drawer">
      {/* A row, not one button: the chat button sits beside Tracks and a button can't hold a button. */}
      <div className="ui-drawer-row">
        <button
          type="button"
          className="ui-drawer-bar"
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
            expand('tracks_drawer', !open);
          }}
        >
          <span className="dt">
            <b>{title}</b>
            <span>{subtitle}</span>
          </span>
          <span className="dbtn">
            {anySoon && <i className="hot" aria-label="A race is about to jump" />}
            Tracks <span className="chev" aria-hidden="true">▴</span>
          </span>
        </button>
        <ChatBarButton />
      </div>
      <div className="ui-drawer-list" aria-hidden={!open}>
        <div>
          {TRACK_TYPES.map(([type, label]) => {
            const ms = meetings.filter((m) => trackTypeOf(m.type) === type);
            if (!ms.length) return null;
            return (
              <div className="tg" key={type}>
                <h4>{label}</h4>
                <div className="tl">
                  {ms.map((m) => {
                    const clock = clockOf(m);
                    return (
                      <Link
                        key={m.key}
                        to={m.href}
                        className={[m.key === currentKey ? 'on' : '', clock ? 'soon' : ''].filter(Boolean).join(' ') || undefined}
                        tabIndex={open ? 0 : -1}
                        onClick={() => {
                          setOpen(false);
                          track('ui_click', { target: 'drawer_track', trackName: m.name });
                        }}
                      >
                        {titleCase(m.name)} <small>{m.state.toUpperCase()}</small>
                        {clock && <span className="cd">{clock}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
